
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [lastSentTimestamp, setLastSentTimestamp] = useState<number | null>(null);
  const [remainingTime, setRemainingTime] = useState(0);
  
  // Update remaining time every second
  useEffect(() => {
    if (!lastSentTimestamp) return;
    
    const calculateRemainingTime = () => {
      const elapsed = Math.floor((Date.now() - lastSentTimestamp) / 1000);
      const remaining = Math.max(0, 60 - elapsed);
      setRemainingTime(remaining);
    };
    
    calculateRemainingTime();
    const timer = setInterval(calculateRemainingTime, 1000);
    
    return () => clearInterval(timer);
  }, [lastSentTimestamp]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check for throttling
    if (lastSentTimestamp && Date.now() - lastSentTimestamp < 60000) {
      toast.error(`Please wait ${remainingTime} seconds before requesting another reset link`);
      return;
    }
    
    setLoading(true);
    try {
      // Use the current origin for the redirect URL
      const redirectUrl = `${window.location.origin}/auth/reset-password`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });
      
      if (error) throw error;
      
      setLastSentTimestamp(Date.now());
      setEmailSent(true);
      toast.success('Reset link sent! Check your email inbox');
    } catch (error: any) {
      if (error.message?.includes('rate limit')) {
        toast.error('Too many requests. Please try again later.');
      } else {
        toast.error(error.message || 'Failed to send reset link');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-slate-50">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center">
            <Link to="/auth/login" className="text-muted-foreground hover:text-foreground mr-2">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <CardTitle className="text-2xl font-bold">Reset Password</CardTitle>
          </div>
          <CardDescription>
            Enter your email to receive a password reset link
          </CardDescription>
        </CardHeader>
        <CardContent>
          {emailSent ? (
            <div className="bg-green-50 text-green-700 text-sm p-4 rounded-md space-y-3">
              <p className="font-medium">Reset link sent!</p>
              <p>We've sent a password reset link to <strong>{email}</strong>. Please check your inbox and follow the instructions.</p>
              <p>The link will expire in 30 minutes.</p>
              
              {remainingTime > 0 && (
                <div className="mt-4 text-sm text-gray-500">
                  <p>You can request another link in {remainingTime} seconds</p>
                </div>
              )}
              
              <div className="mt-4">
                <Button 
                  variant="outline"
                  className="w-full" 
                  onClick={handleSubmit}
                  disabled={loading || remainingTime > 0}
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {remainingTime > 0 ? `Resend in ${remainingTime}s` : 'Resend link'}
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading || remainingTime > 0}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {remainingTime > 0 ? `Resend in ${remainingTime}s` : 'Send Reset Link'}
              </Button>
            </form>
          )}
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <div className="text-sm text-center text-muted-foreground">
            Remembered your password?{" "}
            <Link to="/auth/login" className="text-primary underline-offset-4 hover:underline">
              Sign in
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
